import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Send,
  Loader2,
  Users,
  Building2,
  Wrench,
  Package,
  UserCog,
  CheckCircle,
  X,
  Eye,
  AlertCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BulkEmailModal({
  isOpen,
  onClose,
  token,
  preselectedType = null,
  preselectedHospitalId = null,
  preselectedHospitalName = null
}) {
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [recipientType, setRecipientType] = useState(preselectedType || "all");
  const [hospitalId, setHospitalId] = useState(preselectedHospitalId || "");
  
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isOpen && preselectedType) {
      setRecipientType(preselectedType);
    }
    if (isOpen && preselectedHospitalId) {
      setHospitalId(preselectedHospitalId);
    }
    // Reset state when modal opens
    if (isOpen) {
      setSent(false);
      setShowPreview(false);
      setSelectedIds([]);
      setExcludedIds([]);
    }
  }, [isOpen, preselectedType, preselectedHospitalId]);

  const fetchRecipients = async () => {
    setPreviewing(true);
    try {
      let url = `${API}/admin/bulk-email/recipients-preview?recipient_type=${recipientType}`;
      if (recipientType === "specific_hospital" && hospitalId) {
        url += `&hospital_id=${hospitalId}`;
      }
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Failed to fetch recipients");
      
      const data = await res.json();
      setRecipients(data.recipients || []);
      setShowPreview(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const toggleRecipient = (id) => {
    if (excludedIds.includes(id)) {
      setExcludedIds(prev => prev.filter(i => i !== id));
    } else {
      setExcludedIds(prev => [...prev, id]);
    }
  };

  const selectAll = () => {
    setExcludedIds([]);
  };

  const deselectAll = () => {
    setExcludedIds(recipients.map(r => r.id));
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    const activeRecipients = recipients.filter(r => !excludedIds.includes(r.id));
    if (activeRecipients.length === 0) {
      toast.error("No recipients selected");
      return;
    }
    
    setSending(true);
    try {
      const payload = {
        subject,
        message,
        recipient_type: recipientType,
        hospital_id: recipientType === "specific_hospital" ? hospitalId : null,
        excluded_user_ids: excludedIds.length > 0 ? excludedIds : null
      };
      
      const res = await fetch(`${API}/admin/bulk-email/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to send email");
      }
      
      const data = await res.json();
      toast.success(`Email sent to ${data.recipient_count} recipients!`);
      setSent(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const getRecipientTypeIcon = (type) => {
    switch(type) {
      case "hospitals": return <Building2 className="w-4 h-4" />;
      case "contractors": return <Wrench className="w-4 h-4" />;
      case "suppliers": return <Package className="w-4 h-4" />;
      case "internal": return <UserCog className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case "hospital": return "Hospital";
      case "contractor": return "Contractor";
      case "supplier": return "Supplier";
      case "internal": return "Internal Staff";
      default: return type;
    }
  };

  if (sent) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Email Sent!</h2>
            <p className="text-slate-600 mb-6">
              Your message has been sent to all selected recipients.
            </p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Send Bulk Email
            {preselectedHospitalName && (
              <span className="text-sm font-normal text-slate-500">
                - {preselectedHospitalName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Recipient Type Selection */}
          {!preselectedType && (
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Send To
              </Label>
              <Select value={recipientType} onValueChange={setRecipientType}>
                <SelectTrigger data-testid="recipient-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" /> All Users
                    </div>
                  </SelectItem>
                  <SelectItem value="hospitals">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> All Hospital Users
                    </div>
                  </SelectItem>
                  <SelectItem value="contractors">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-4 h-4" /> All Contractors
                    </div>
                  </SelectItem>
                  <SelectItem value="suppliers">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" /> All Suppliers
                    </div>
                  </SelectItem>
                  <SelectItem value="internal">
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4" /> Internal Staff Only
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Subject */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Subject *
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              data-testid="email-subject-input"
            />
          </div>

          {/* Message */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Message *
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message here..."
              rows={6}
              data-testid="email-message-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              Supports line breaks. HTML formatting is not supported.
            </p>
          </div>

          {/* Preview Recipients Button */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={fetchRecipients}
              disabled={previewing}
              data-testid="preview-recipients-btn"
            >
              {previewing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Eye className="w-4 h-4 mr-2" />
              )}
              Preview Recipients
            </Button>
            
            {showPreview && (
              <span className="text-sm text-slate-600">
                {recipients.length - excludedIds.length} of {recipients.length} selected
              </span>
            )}
          </div>

          {/* Recipients Preview */}
          {showPreview && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-4 py-2 border-b flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  Recipients ({recipients.length})
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                    className="text-xs"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deselectAll}
                    className="text-xs"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {recipients.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    No recipients found
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {recipients.map((recipient) => (
                      <div
                        key={recipient.id}
                        className={`px-4 py-2 flex items-center justify-between hover:bg-slate-50 ${
                          excludedIds.includes(recipient.id) ? "opacity-50 bg-slate-100" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={!excludedIds.includes(recipient.id)}
                            onCheckedChange={() => toggleRecipient(recipient.id)}
                          />
                          <div>
                            <p className="text-sm font-medium text-slate-800">
                              {recipient.name}
                            </p>
                            <p className="text-xs text-slate-500">{recipient.email}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          recipient.type === "hospital" ? "bg-blue-100 text-blue-700" :
                          recipient.type === "contractor" ? "bg-amber-100 text-amber-700" :
                          recipient.type === "supplier" ? "bg-purple-100 text-purple-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {getTypeLabel(recipient.type)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !subject.trim() || !message.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-email-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
